/* eslint-disable react-hooks/exhaustive-deps */
/**
 * @name Company Hook
 * @memberof module:common/hooks
 * @type {ReactHook}
 * @return {React} Firm Hook
 */
import React from 'react';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { FirmCompany as CreatedFirmCompany } from 'api';
import { companiesAction, globalAction } from 'common/actions';
import { ERROR_409 } from 'common/config/api';
import { useStore } from 'common/store';
import { FirmCompany } from 'common/types/company';
import { UseStoreValues } from 'common/types/store';
import { FirmService } from '..';
import useResponse from './useResponse';


/**
 * @function
 * @name useCreateFirmCompany
 * @memberof Firm Hook
 * @description Hook to create a new company for specific firm
 * @param {OBJECT} companyData the company information
 * @return {OBJECT} structure for Hook context.
 */
export const useCreateFirmCompany = (): {
  data: CreatedFirmCompany | null;
  createCompany: (data: any) => Promise<CreatedFirmCompany | null>;
} => {
  const [storeValues, dispatch] = useStore() as unknown as UseStoreValues;
  const [data, setData] = useState<CreatedFirmCompany | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { processErrorResponse } = useResponse();
  const defaultErrorMessage = 'An error occurred while creating the Company';
  const errorMessages = {
    [ERROR_409]: 'A company with this name already exists.',
  };
  const { firmId } = storeValues;

  const createCompany = async (companyData: any): Promise<CreatedFirmCompany | null> => {
    dispatch(globalAction.showLoadingProgress(true));
    try {
      const firmService = new FirmService();
      const newCompany = await firmService.createFirmCompany(companyData);
      
      if (newCompany?.id) {
        enqueueSnackbar(`${newCompany.name} successfully created`, { variant: 'success' });
        const newCompanyList = await firmService.getCompanyListByFirmId(newCompany.firm ?? firmId);

        dispatch(companiesAction.setCompanyList(newCompanyList as FirmCompany[]));
        setData(newCompany);
        return newCompany;
      } 
      return null;
      
    } catch (error: any) {
      const { status } = error;
      const servicePath = error.response.req.url.split('/').pop();
      const action = servicePath === 'new' ? 'create a new company' : 'get companies';
      processErrorResponse({
        error,
        defaultErrorMessage: errorMessages[status as keyof typeof errorMessages] ?? defaultErrorMessage,
        action,
      });
      return null;
    } finally {
      dispatch(globalAction.showLoadingProgress(false));
    }
  };



  return { data, createCompany };
};
